"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { BiArrowBack, BiSearch, BiX } from "react-icons/bi";
import { FaEye } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getPendingWithdrawals,
  getWithdrawalDetails,
  updatePayoutStatus,
  getEarningPayoutStats,
} from "../../redux/thunk";
import { PendingWithdrawal } from "../../types/payout";
import toast from "react-hot-toast";

// Custom Components (same as in other files)
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode;
  variant?: "default" | "ghost" | "destructive" | "secondary";
  size?: "default" | "sm";
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  };

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export const PayoutRequests = () => {
  const dispatch = useAppDispatch();
  const {
    pendingWithdrawals,
    currentWithdrawalDetails,
    payoutStats,
    payoutPagination,
    payoutStatus,
    payoutError,
    payoutApproveStatus,
    payoutRejectStatus,
    payoutDetailsStatus,
  } = useAppSelector((store) => store.logistics);
  
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<PendingWithdrawal | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [rejectReason, setRejectReason] = useState("");
  
  // Load data on component mount
  useEffect(() => {
    dispatch(getPendingWithdrawals({ page: currentPage, limit: itemsPerPage }));
    dispatch(getEarningPayoutStats());
  }, [dispatch, currentPage, itemsPerPage]);
  
  // Filter withdrawals based on search and status
  const filteredWithdrawals = pendingWithdrawals.filter((withdrawal) => {
    const matchesSearch = withdrawal.riderName
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
      withdrawal.withdrawalId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "All Statuses" || 
      withdrawal.status.toLowerCase() === statusFilter.toLowerCase().replace(' approval', '').replace(' ', '');
    
    return matchesSearch && matchesStatus;
  });

  const handlePayoutDetails = async (payout: PendingWithdrawal) => {
    setSelectedPayout(payout);
    setShowPayoutModal(true);
    // Fetch detailed withdrawal information
    dispatch(getWithdrawalDetails({ 
      riderId: payout.riderId, 
      withdrawalId: payout.withdrawalId 
    }));
  };

  const handleApproveRequest = async () => {
    if (!selectedPayout) return;
    
    try {
      await dispatch(updatePayoutStatus({
        riderId: selectedPayout.riderId,
        withdrawalId: selectedPayout.withdrawalId,
        data: { action: 'approve' }
      })).unwrap();
      
      setShowPayoutModal(false);
      setSelectedPayout(null);
      // Refresh the list
      dispatch(getPendingWithdrawals({ page: currentPage, limit: itemsPerPage }));
      dispatch(getEarningPayoutStats());
    } catch (error) {
      console.error('Failed to approve payout:', error);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedPayout || !rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    
    try {
      await dispatch(updatePayoutStatus({
        riderId: selectedPayout.riderId,
        withdrawalId: selectedPayout.withdrawalId,
        data: { action: 'reject', reason: rejectReason }
      })).unwrap();
      
      setShowPayoutModal(false);
      setSelectedPayout(null);
      setRejectReason("");
      // Refresh the list
      dispatch(getPendingWithdrawals({ page: currentPage, limit: itemsPerPage }));
      dispatch(getEarningPayoutStats());
    } catch (error) {
      console.error('Failed to reject payout:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      "Pending Approval": "bg-yellow-100 text-yellow-800",
      Approved: "bg-green-100 text-green-800",
      Rejected: "bg-red-100 text-red-800",
    };
    return (
      statusStyles[status as keyof typeof statusStyles] ||
      "bg-gray-100 text-gray-800"
    );
  };


  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header className="mb-4 h-16 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BiArrowBack 
            className="w-6 h-6 text-gray-600 cursor-pointer" 
            onClick={() => window.history.back()} 
          />
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Logistics</span>
              <span>›</span>
              <span>Payout Requests</span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Payout Requests
            </h2>
            <p className="text-sm text-gray-500">
              Monitor vendor earnings, process payouts, and manage financial adjustments.
            </p>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-blue-600 text-sm font-medium mb-1">Total Withdrawals</div>
          <div className="text-2xl font-semibold text-blue-800">{payoutStats?.totalWithdrawals || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-yellow-600 text-sm font-medium mb-1">Total Pending</div>
          <div className="text-2xl font-semibold text-yellow-800">₦{payoutStats?.totalPending?.toLocaleString() || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-green-600 text-sm font-medium mb-1">Total Approved</div>
          <div className="text-2xl font-semibold text-green-800">₦{payoutStats?.totalApproved?.toLocaleString() || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-purple-600 text-sm font-medium mb-1">Average Approved</div>
          <div className="text-2xl font-semibold text-purple-800">₦{payoutStats?.avgApproved?.toFixed(2).toLocaleString() || 0}</div>
        </div>
      </div>

      {/* Loading and Error States */}
      {payoutStatus === 'loading' && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-2">Loading payouts...</span>
        </div>
      )}

      {payoutError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          Error: {payoutError}
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex items-center gap-x-6 justify-between mb-6">
        <div className="relative w-full">
          <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search Rider by name or payout ID"
            className="pl-10 h-10 w-[500px]"
            value={searchTerm}
            onChange={(e: {
              target: { value: React.SetStateAction<string> };
            }) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-md border border-input px-3 py-2 text-sm"
          >
            <option>All Statuses</option>
            <option>Pending Approval</option>
            <option>Approved</option>
            <option>Rejected</option>
          </select>
        </div>
      </div>

      {/* Payout Requests Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requested Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredWithdrawals.map((withdrawal) => (
                <tr key={withdrawal.withdrawalId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {withdrawal.riderName}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {withdrawal.withdrawalId.substring(0, 8)}...
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      ₦{withdrawal.requestedAmount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {withdrawal.dateOfRequest
                        ? new Date(withdrawal.dateOfRequest).toLocaleDateString()
                        : "N/A"
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge className={getStatusBadge(withdrawal.status)}>
                      {withdrawal.status === 'pending' ? 'Pending Approval' : withdrawal.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-600 rounded-lg border border-green-600 space-x-2"
                      onClick={() => handlePayoutDetails(withdrawal)}
                      disabled={payoutDetailsStatus === 'loading'}
                    >
                      <span>Review Request</span>
                      <FaEye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredWithdrawals.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    {searchTerm || statusFilter !== "All Statuses"
                      ? "No payout requests match your search criteria"
                      : "No payout requests found"
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Request Details Modal */}
      {showPayoutModal && selectedPayout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">
                  Payout Details - {selectedPayout.withdrawalId.substring(0, 8)}...
                </h3>
                <p className="text-sm text-gray-500">
                  View detailed breakdown of earnings and payout information for {selectedPayout.riderName}.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowPayoutModal(false);
                  setSelectedPayout(null);
                }}
              >
                <BiX className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Loading state for details */}
            {payoutDetailsStatus === 'loading' && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                <span className="ml-2">Loading details...</span>
              </div>
            )}

            {payoutDetailsStatus !== 'loading' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Requested Amount:</p>
                    <p className="text-lg font-bold">₦{currentWithdrawalDetails?.requestedAmount?.toLocaleString() || selectedPayout.requestedAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Available Balance:</p>
                    <p className="text-lg font-bold text-green-600">₦{currentWithdrawalDetails?.availableBalance?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Request Date:</p>
                    <p className="text-sm text-gray-500">
                      {selectedPayout.dateOfRequest
                        ? new Date(selectedPayout.dateOfRequest).toLocaleDateString()
                        : "N/A"
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Period:</p>
                    <p className="text-sm text-gray-500">{currentWithdrawalDetails?.period || "N/A"}</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium mb-3">Earnings Breakdown:</h5>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium">
                          Base Earnings:
                        </p>
                        <p className="text-lg font-bold">{currentWithdrawalDetails?.earningsBreakdown?.baseEarning || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Distance Bonus:</p>
                        <p className="text-lg font-bold">₦{currentWithdrawalDetails?.earningsBreakdown?.distanceBonus?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Customer Tip:</p>
                        <p className="text-lg font-bold">₦{currentWithdrawalDetails?.earningsBreakdown?.customerTip?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-medium">Requested Amount:</span>
                        <span className="text-2xl font-bold text-green-600">
                          ₦{currentWithdrawalDetails?.requestedAmount?.toLocaleString() || selectedPayout.requestedAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium mb-3">Account Details:</h5>
                  {currentWithdrawalDetails?.bankDetails && currentWithdrawalDetails.bankDetails.length > 0 ? (
                    currentWithdrawalDetails.bankDetails.map((bank, index) => (
                      <div key={index} className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium">Account Number:</p>
                          <p className="text-sm text-gray-500">{bank.accountNumber}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Account Name:</p>
                          <p className="text-sm text-gray-500">{bank.accountName}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Bank:</p>
                          <p className="text-sm text-gray-500">{bank.bankName}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No bank details available</p>
                  )}
                </div>
                
                {/* Reject reason input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason (required for rejection):
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    className="flex-1 bg-green-600 text-white hover:bg-green-700"
                    onClick={handleApproveRequest}
                    disabled={payoutApproveStatus === 'loading' || payoutRejectStatus === 'loading'}
                  >
                    {payoutApproveStatus === 'loading' ? 'Approving...' : 'Approve Payout'}
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={handleRejectRequest}
                    disabled={payoutApproveStatus === 'loading' || payoutRejectStatus === 'loading' || !rejectReason.trim()}
                  >
                    {payoutRejectStatus === 'loading' ? 'Rejecting...' : 'Reject Request'}
                  </Button>
                  <Button
                    variant="secondary"
                    className="px-8"
                    onClick={() => {
                      setShowPayoutModal(false);
                      setSelectedPayout(null);
                      setRejectReason("");
                    }}
                    disabled={payoutApproveStatus === 'loading' || payoutRejectStatus === 'loading'}
                  >
                    Back
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};